/**
 * Student Journey Tracker Component
 * Displays application progress through different stages based on user role
 */
import { useState } from 'react';
import { 
  CheckCircle, 
  Circle, 
  ChevronRight, 
  Clock, 
  User, 
  FileText,
  Send,
  Award,
  Wallet,
  Building,
  Plane,
  GraduationCap,
  DollarSign,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { applicationsAPI } from '@/lib/api';

// Stage definitions with icons and colors
const STAGE_CONFIG = {
  inquiry: { 
    label: 'Inquiry', 
    icon: User, 
    color: 'text-gray-500',
    bgColor: 'bg-gray-200',
    description: 'Initial inquiry received'
  },
  lead_validated: { 
    label: 'Lead Validated', 
    icon: CheckCircle, 
    color: 'text-blue-600',
    bgColor: 'bg-blue-400/20',
    description: 'Lead has been validated'
  },
  application_received: { 
    label: 'Application Received', 
    icon: FileText, 
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-400/20',
    description: 'Application documents received'
  },
  application_submitted: { 
    label: 'Application Submitted', 
    icon: Send, 
    color: 'text-indigo-400',
    bgColor: 'bg-indigo-400/20',
    description: 'Application submitted to university'
  },
  offer_issued: { 
    label: 'Offer Issued', 
    icon: Award, 
    color: 'text-purple-600',
    bgColor: 'bg-purple-400/20',
    description: 'University has issued an offer'
  },
  offer_accepted: { 
    label: 'Offer Accepted', 
    icon: CheckCircle, 
    color: 'text-pink-600',
    bgColor: 'bg-pink-400/20',
    description: 'Student has accepted the offer'
  },
  uco: { 
    label: 'UCO', 
    icon: Award, 
    color: 'text-amber-600',
    bgColor: 'bg-amber-400/20',
    description: 'Unconditional Offer received'
  },
  deposit: { 
    label: 'Deposit', 
    icon: Wallet, 
    color: 'text-orange-600',
    bgColor: 'bg-orange-400/20',
    description: 'Deposit has been paid'
  },
  cas_stage: { 
    label: 'CAS Stage', 
    icon: Building, 
    color: 'text-teal-400',
    bgColor: 'bg-teal-100',
    description: 'CAS letter issued'
  },
  refund: {
    label: 'Refund',
    icon: DollarSign,
    color: 'text-rose-500',
    bgColor: 'bg-rose-100',
    description: 'Refund processed'
  },
  visa_issued: { 
    label: 'Visa Issued', 
    icon: Plane, 
    color: 'text-sky-400',
    bgColor: 'bg-sky-400/20',
    description: 'Student visa approved'
  },
  enrolment_completed: { 
    label: 'Enrolled', 
    icon: GraduationCap, 
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-400/20',
    description: 'Student has enrolled at university'
  },
  commission_received: { 
    label: 'Commission', 
    icon: DollarSign, 
    color: 'text-teal-600',
    bgColor: 'bg-teal-600/20',
    description: 'Commission has been received'
  },
};

// Stage lists for different user roles
const ADMIN_STAGES = [
  'inquiry', 'lead_validated', 'application_received', 'application_submitted',
  'offer_issued', 'offer_accepted', 'uco', 'deposit', 'cas_stage',
  'refund',
  'visa_issued', 'enrolment_completed', 'commission_received'
];

const AGENT_STAGES = [
  'application_received', 'application_submitted', 'offer_issued', 'offer_accepted',
  'uco', 'deposit', 'cas_stage', 'refund', 'visa_issued', 'enrolment_completed', 'commission_received'
];

const STUDENT_STAGES = [
  'application_submitted', 'offer_issued', 'offer_accepted', 'uco',
  'deposit', 'cas_stage', 'refund', 'visa_issued', 'enrolment_completed'
];

export const JourneyTracker = ({ 
  application, 
  userRole = 'student', 
  onStageUpdate = null,
  compact = false 
}) => {
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [notes, setNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Get stages based on user role
  const stages = userRole === 'admin' ? ADMIN_STAGES : 
                 userRole === 'agent' ? AGENT_STAGES : STUDENT_STAGES;

  const currentStage = application?.journey_stage || 'inquiry';
  const currentStageIndex = stages.indexOf(currentStage);
  const journeyHistory = application?.journey_history || [];

  // Check if a stage is completed
  const isStageCompleted = (stage) => {
    const stageIndex = stages.indexOf(stage);
    return stageIndex <= currentStageIndex;
  };

  // Check if a stage is the current one
  const isCurrentStage = (stage) => {
    return stage === currentStage;
  };

  // Handle stage click (admin only)
  const handleStageClick = (stage) => {
    if (userRole !== 'admin') return;
    setSelectedStage(stage);
    setNotes('');
    setShowUpdateDialog(true);
  };

  // Update stage
  const handleUpdateStage = async () => {
    if (!selectedStage) return;
    setUpdating(true);
    try {
      await applicationsAPI.updateJourneyStage(application.id, selectedStage, notes || null);
      toast.success(`Stage updated to ${STAGE_CONFIG[selectedStage]?.label || selectedStage}`);
      setShowUpdateDialog(false);
      if (onStageUpdate) onStageUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    } finally {
      setUpdating(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Compact view for mobile/list views
  if (compact) {
    const completedCount = stages.filter(s => isStageCompleted(s)).length;
    const progress = (completedCount / stages.length) * 100;
    const config = STAGE_CONFIG[currentStage] || STAGE_CONFIG.inquiry;
    const Icon = config.icon;

    return (
      <div className="bg-[#e9ecef]/30 rounded-lg p-3 border border-white/5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center`}>
              <Icon className={`w-4 h-4 ${config.color}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">{config.label}</p>
              <p className="text-xs text-gray-500">{completedCount}/{stages.length} stages</p>
            </div>
          </div>
          <Badge className={`${config.bgColor} ${config.color} text-xs`}>
            {Math.round(progress)}%
          </Badge>
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="bg-[#e9ecef]/30 rounded-xl p-4 sm:p-6 border border-gray-200" data-testid="journey-tracker">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-teal-600" />
            Application Journey
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {application?.course_name} at {application?.university_name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {userRole === 'admin' && (
            <Badge className="bg-teal-600/20 text-teal-600">Admin View</Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="text-gray-500 hover:text-gray-800"
          >
            <Clock className="w-4 h-4 mr-1" />
            History
            {showHistory ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>

      {/* Journey Progress */}
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 hidden sm:block" />
        <div 
          className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-yellow-400 to-yellow-500 hidden sm:block transition-all duration-500"
          style={{ width: `${(currentStageIndex / (stages.length - 1)) * 100}%` }}
        />

        {/* Stages */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-1">
          {stages.map((stage, index) => {
            const config = STAGE_CONFIG[stage] || STAGE_CONFIG.inquiry;
            const Icon = config.icon;
            const completed = isStageCompleted(stage);
            const current = isCurrentStage(stage);
            const canClick = userRole === 'admin';

            return (
              <div 
                key={stage}
                className={`flex sm:flex-col items-center gap-3 sm:gap-2 sm:flex-1 group ${canClick ? 'cursor-pointer' : ''}`}
                onClick={() => canClick && handleStageClick(stage)}
              >
                {/* Stage Icon */}
                <div className={`
                  relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                  ${completed ? config.bgColor : 'bg-gray-200'}
                  ${current ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-slate-900' : ''}
                  ${canClick ? 'group-hover:scale-110' : ''}
                `}>
                  {completed ? (
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400" />
                  )}
                </div>

                {/* Stage Label */}
                <div className="sm:text-center flex-1 sm:flex-none">
                  <p className={`text-xs sm:text-[10px] font-medium truncate sm:whitespace-normal ${
                    completed ? 'text-gray-800' : 'text-gray-400'
                  }`}>
                    {config.label}
                  </p>
                  {/* Show date for completed stages */}
                  {completed && journeyHistory.find(h => h.stage === stage) && (
                    <p className="text-[10px] text-gray-400 hidden sm:block">
                      {formatDate(journeyHistory.find(h => h.stage === stage)?.completed_at).split(',')[0]}
                    </p>
                  )}
                </div>

                {/* Arrow for mobile */}
                {index < stages.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-slate-600 sm:hidden" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* History Section */}
      {showHistory && journeyHistory.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-800 mb-3">Journey History</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {journeyHistory.slice().reverse().map((entry, index) => {
              const config = STAGE_CONFIG[entry.stage] || STAGE_CONFIG.inquiry;
              const Icon = config.icon;
              return (
                <div key={index} className="flex items-start gap-3 text-sm">
                  <div className={`w-8 h-8 rounded-full ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-800 font-medium">{config.label}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(entry.completed_at)} by {entry.completed_by_name || 'System'}
                    </p>
                    {entry.notes && (
                      <p className="text-xs text-gray-400 mt-1">{entry.notes}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Update Stage Dialog (Admin Only) */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              Update Journey Stage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedStage && (
              <div className="bg-[#e9ecef]/50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {(() => {
                    const config = STAGE_CONFIG[selectedStage] || STAGE_CONFIG.inquiry;
                    const Icon = config.icon;
                    return (
                      <>
                        <div className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${config.color}`} />
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">{config.label}</p>
                          <p className="text-xs text-gray-500">{config.description}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-gray-600">Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-[#e9ecef] border-gray-200 min-h-[80px]"
                placeholder="Add notes about this stage update..."
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowUpdateDialog(false)}
                className="flex-1 border-gray-300 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateStage}
                disabled={updating}
                className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold"
              >
                {updating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Update Stage'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JourneyTracker;
